import { CircleLayer, LineLayer } from '@trimblemaps/trimblemaps-js';
import { FeatureTypes, MapLayers, MapSources } from '../enums';
import { isVisibleUtil } from '../mapUtils';
import { RecommendationType } from '../../enums';

/**
 * Creates a layer to display circles on the map for each Point and 'featureType' of 'laneCenter' in the given map source.
 * Adds the lane center layer to the map if it doesn't already exist
 * @param map Instance of your map
 * @param mapSource Name of the map source
 * @param isVisible Indicator if the map layer is visible
 */
export const addLaneCenterLayer = ({
    map,
    mapSource,
    isVisible,
}: {
    map: TrimbleMaps.Map;
    mapSource: MapSources;
    isVisible: boolean;
}): void => {
    const visibility = isVisibleUtil(isVisible);

    const layer: CircleLayer = {
        id: MapLayers.laneCenterLayer,
        type: 'circle',
        source: mapSource,
        layout: {
            visibility,
        },
        paint: {
            'circle-color': ['get', 'color'],
            'circle-opacity': [
                'case',
                ['boolean', ['get', 'isHovered'], true],
                1.0,
                0.5,
            ],
            'circle-radius': 3,
            'circle-stroke-width': 2,
            'circle-stroke-color': ['get', 'strokeColor'],
        },
        filter: ['all',
            ['==', '$type', 'Point'],
            ['==', 'featureType', FeatureTypes.LaneCenter],
        ],
    };

    if (map.getLayer(MapLayers.laneCenterLayer) === undefined) {
        map.addLayer(layer);
    }
};

/**
 * Creates a layer to display lines on the map for each LineString and 'featureType' of 'laneLine' in the given map source.
 * Adds the lane line layer to the map if it doesn't already exist
 * @param map Instance of your map
 * @param mapSource Name of the map source
 * @param isVisible Indicator if the map layer is visible
 */
export const addLaneLineLayer = ({
    map,
    mapSource,
    isVisible,
}: {
    map: TrimbleMaps.Map;
    mapSource: MapSources;
    isVisible: boolean;
}): void => {
    const visibility = isVisibleUtil(isVisible);

    const layer: LineLayer = {
        id: MapLayers.laneLineLayer,
        type: 'line',
        source: mapSource,
        layout: {
            'line-join': 'round',
            'line-cap': 'round',
            visibility,
        },
        paint: {
            'line-color': ['get', 'color'],
            'line-width': [
                'case',
                ['boolean', ['get', 'isHovered'], true],
                4,
                3,
            ],
            'line-opacity': [
                'case',
                ['boolean', ['get', 'isHovered'], true],
                0.8,
                0.4,
            ],
            'line-offset': 0,
        },
        filter: ['all',
            ['==', '$type', 'LineString'],
            ['==', 'featureType', FeatureTypes.LaneLine],
        ],
    };

    if (map.getLayer(MapLayers.laneLineLayer) === undefined) {
        map.addLayer(layer);
    }
};

/**
 * Creates a layer to display lines on the map for each LineString and 'featureType' of 'laneLine' in the given map source.
 * Adds the lane line layer to the map if it doesn't already exist
 * @param map Instance of your map
 * @param mapSource Name of the map source
 * @param isVisible Indicator if the map layer is visible
 */
export const addLaneLineHoverLayer = ({
    map,
    mapSource,
    isVisible,
}: {
    map: TrimbleMaps.Map;
    mapSource: MapSources;
    isVisible: boolean;
}): void => {
    const visibility = isVisibleUtil(isVisible);

    const layer: LineLayer = {
        id: MapLayers.laneLineHoverLayer,
        type: 'line',
        source: mapSource,
        layout: {
            'line-join': 'round',
            'line-cap': 'round',
            visibility,
        },
        paint: {
            'line-color': ['get', 'color'],
            'line-width': 12,
            'line-opacity': 0,
            'line-offset': 0,
        },
        filter: ['all',
            ['==', '$type', 'LineString'],
            ['==', 'featureType', FeatureTypes.LaneLine],
        ],
    };

    if (map.getLayer(MapLayers.laneLineHoverLayer) === undefined) {
        map.addLayer(layer);
    }
};

/**
 * Creates a layer to display circles on the map for each Point and 'featureType' of 'networkRecommendationLaneCenter' in the given map source.
 * Adds the lane center layer to the map if it doesn't already exist
 * @param map Instance of your map
 * @param mapSource Name of the map source
 * @param isVisible Indicator if the map layer is visible
 * @param selectedRecommendationType Selected recommendation type to show on map
 */
export const addNetworkRecommendationLaneCenterLayer = ({
    map,
    mapSource,
    isVisible,
    selectedRecommendationType,
}: {
    map: TrimbleMaps.Map;
    mapSource: MapSources;
    isVisible: boolean;
    selectedRecommendationType: RecommendationType;
}): void => {
    const visibility = isVisibleUtil(isVisible);

    const layer: CircleLayer = {
        id: MapLayers.networkRecommendationLaneCenterLayer,
        type: 'circle',
        source: mapSource,
        layout: {
            visibility,
        },
        paint: {
            'circle-color': ['get', 'color'],
            'circle-opacity': [
                'case',
                ['boolean', ['get', 'isHovered'], true],
                1.0,
                0.5,
            ],
            'circle-radius': 4,
            'circle-stroke-width': 2,
            'circle-stroke-color': ['get', 'strokeColor'],
        },
        filter: ['all',
            ['==', '$type', 'Point'],
            ['==', 'featureType', FeatureTypes.NetworkRecommendationLaneCenter],
            ['==', 'recommendationType', selectedRecommendationType],
        ],
    };

    if (map.getLayer(MapLayers.networkRecommendationLaneCenterLayer) === undefined) {
        map.addLayer(layer);
    }
};

/**
 * Creates a layer to display lines on the map for each LineString and 'featureType' of 'networkRecommendationLaneLine' in the given map source.
 * Adds the lane line layer to the map if it doesn't already exist
 * @param map Instance of your map
 * @param mapSource Name of the map source
 * @param isVisible Indicator if the map layer is visible
 * @param selectedRecommendationType Selected recommendation type to show on map
 */
export const addNetworkRecommendationLaneLineLayer = ({
    map,
    mapSource,
    isVisible,
    selectedRecommendationType,
}: {
    map: TrimbleMaps.Map;
    mapSource: MapSources;
    isVisible: boolean;
    selectedRecommendationType: RecommendationType;
}): void => {
    const visibility = isVisibleUtil(isVisible);

    const layer: LineLayer = {
        id: MapLayers.networkRecommendationLaneLineLayer,
        type: 'line',
        source: mapSource,
        layout: {
            'line-join': 'round',
            'line-cap': 'round',
            visibility,
        },
        paint: {
            'line-color': ['get', 'color'],
            'line-width': [
                'case',
                ['boolean', ['get', 'isHovered'], true],
                7,
                5,
            ],
            'line-opacity': [
                'case',
                ['boolean', ['get', 'isHovered'], true],
                1.0,
                0.5,
            ],
            'line-offset': 0,
        },
        filter: ['all',
            ['==', '$type', 'LineString'],
            ['==', 'featureType', FeatureTypes.NetworkRecommendationLaneLine],
            ['==', 'recommendationType', selectedRecommendationType],
        ],
    };

    if (map.getLayer(MapLayers.networkRecommendationLaneLineLayer) === undefined) {
        map.addLayer(layer);
    }
};

/**
 * Creates a layer to display lines on the map for each LineString and 'featureType' of 'networkRecommendationLaneLine' in the given map source.
 * Adds the lane line layer to the map if it doesn't already exist
 * @param map Instance of your map
 * @param mapSource Name of the map source
 * @param isVisible Indicator if the map layer is visible
 * @param selectedRecommendationType Selected recommendation type to show on map
 */
export const addNetworkRecommendationLaneLineHoverLayer = ({
    map,
    mapSource,
    isVisible,
    selectedRecommendationType,
}: {
    map: TrimbleMaps.Map;
    mapSource: MapSources;
    isVisible: boolean;
    selectedRecommendationType: RecommendationType;
}): void => {
    const visibility = isVisibleUtil(isVisible);

    const layer: LineLayer = {
        id: MapLayers.networkRecommendationLaneLineHoverLayer,
        type: 'line',
        source: mapSource,
        layout: {
            'line-join': 'round',
            'line-cap': 'round',
            visibility,
        },
        paint: {
            'line-color': ['get', 'color'],
            'line-width': 12,
            'line-opacity': 0,
            'line-offset': 0,
        },
        filter: ['all',
            ['==', '$type', 'LineString'],
            ['==', 'featureType', FeatureTypes.NetworkRecommendationLaneLine],
            ['==', 'recommendationType', selectedRecommendationType],
        ],
    };

    if (map.getLayer(MapLayers.networkRecommendationLaneLineHoverLayer) === undefined) {
        map.addLayer(layer);
    }
};

/**
 * Creates a layer to display circles on the map for each Point and 'featureType' of 'selectedRecommendationLaneCenter' in the given map source.
 * Adds the lane center layer to the map if it doesn't already exist
 * @param map Instance of your map
 * @param mapSource Name of the map source
 * @param isVisible Indicator if the map layer is visible
 */
export const addSelectedRecommendationLaneCenterLayer = ({
    map,
    mapSource,
    isVisible,
}: {
    map: TrimbleMaps.Map;
    mapSource: MapSources;
    isVisible: boolean;
}): void => {
    const visibility = isVisibleUtil(isVisible);

    const layer: CircleLayer = {
        id: MapLayers.selectedRecommendationLaneCenterLayer,
        type: 'circle',
        source: mapSource,
        layout: {
            visibility,
        },
        paint: {
            'circle-color': ['get', 'color'],
            'circle-opacity': [
                'case',
                ['boolean', ['get', 'isHovered'], true],
                1.0,
                0.5,
            ],
            'circle-radius': 6,
            'circle-stroke-width': 3,
            'circle-stroke-color': ['get', 'strokeColor'],
        },
        filter: ['all',
            ['==', '$type', 'Point'],
            ['==', 'featureType', FeatureTypes.SelectedRecommendationLaneCenter],
        ],
    };

    if (map.getLayer(MapLayers.selectedRecommendationLaneCenterLayer) === undefined) {
        map.addLayer(layer);
    }
};

/**
 * Creates a layer to display lines on the map for each LineString and 'featureType' of 'selectedRecommendationLaneLine' in the given map source.
 * Adds the lane line layer to the map if it doesn't already exist
 * @param map Instance of your map
 * @param mapSource Name of the map source
 * @param isVisible Indicator if the map layer is visible
 */
export const addSelectedRecommendationLaneLineLayer = ({
    map,
    mapSource,
    isVisible,
}: {
    map: TrimbleMaps.Map;
    mapSource: MapSources;
    isVisible: boolean;
}): void => {
    const visibility = isVisibleUtil(isVisible);

    const layer: LineLayer = {
        id: MapLayers.selectedRecommendationLaneLineLayer,
        type: 'line',
        source: mapSource,
        layout: {
            'line-join': 'round',
            'line-cap': 'round',
            visibility,
        },
        paint: {
            'line-color': ['get', 'color'],
            'line-width': [
                'case',
                ['boolean', ['get', 'isHovered'], true],
                7,
                5,
            ],
            'line-opacity': [
                'case',
                ['boolean', ['get', 'isHovered'], true],
                1.0,
                0.5,
            ],
            'line-offset': 0,
        },
        filter: ['all',
            ['==', '$type', 'LineString'],
            ['==', 'featureType', FeatureTypes.SelectedRecommendationLaneLine],
        ],
    };

    if (map.getLayer(MapLayers.selectedRecommendationLaneLineLayer) === undefined) {
        map.addLayer(layer);
    }
};

/**
 * Creates a layer to display lines on the map for each LineString and 'featureType' of 'selectedRecommendationLaneLine' in the given map source.
 * Adds the lane line layer to the map if it doesn't already exist
 * @param map Instance of your map
 * @param mapSource Name of the map source
 * @param isVisible Indicator if the map layer is visible
 */
export const addSelectedRecommendationLaneLineHoverLayer = ({
    map,
    mapSource,
    isVisible,
}: {
    map: TrimbleMaps.Map;
    mapSource: MapSources;
    isVisible: boolean;
}): void => {
    const visibility = isVisibleUtil(isVisible);

    const layer: LineLayer = {
        id: MapLayers.selectedRecommendationLaneLineHoverLayer,
        type: 'line',
        source: mapSource,
        layout: {
            'line-join': 'round',
            'line-cap': 'round',
            visibility,
        },
        paint: {
            'line-color': ['get', 'color'],
            'line-width': 12,
            'line-opacity': 0,
            'line-offset': 0,
        },
        filter: ['all',
            ['==', '$type', 'LineString'],
            ['==', 'featureType', FeatureTypes.SelectedRecommendationLaneLine],
        ],
    };

    if (map.getLayer(MapLayers.selectedRecommendationLaneLineHoverLayer) === undefined) {
        map.addLayer(layer);
    }
};
